import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { getLoginReport, removePortal, userView } from '../../redux/actions/userAction';
import { useNavigate, useParams } from 'react-router-dom';
import { AiOutlineArrowLeft } from 'react-icons/ai';
import errorToast from '../../components/toast/errorToast';
import successToast from '../../components/toast/successToast';
import Loading from '../../components/ui/loader/loader';
import moment from 'moment';
import exportFromJSON from "export-from-json";
import './users.css';
import { adminGetUserSessionsApi, adminGetUserSessionHistoryApi, adminTerminateSessionApi, adminTerminateAllSessionsApi } from '../../redux/apis/sessionApis';

const UserView = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();
    const [loading, setLoading] = useState(false);
    const [userData, setUserData] = useState({});
    const [showAll, setShowAll] = useState(false);
    const [sessions, setSessions] = useState([]);
    const [loadingSessions, setLoadingSessions] = useState(false);
    const [terminatingId, setTerminatingId] = useState(null);
    const [terminatingAll, setTerminatingAll] = useState(false);
    const [activeTab, setActiveTab] = useState('active');
    const [historyPage, setHistoryPage] = useState(1);
    const [historyData, setHistoryData] = useState({ sessions: [], total: 0, page: 1, totalPages: 0 });
    const [loadingHistory, setLoadingHistory] = useState(false);

    const onSuccess = (data) => {
        setUserData(data.data.data);
        setLoading(false);
    };

    const onError = (data) => {
        errorToast(data.data.message);
        setLoading(false);
    };

    useEffect(() => {
        setLoading(true);
        dispatch(userView(id, onSuccess, onError));
    }, [id, dispatch]);

    const downLoadReport = () => {
        dispatch(getLoginReport(id, onSuccessReport, onError));
    };

    const onSuccessReport = (data) => {
        exportFromJSON({ data: data.data.data, fileName: "loginreport", exportType: exportFromJSON.types.csv });
    };

    const handleRemove = (itemId) => {
        dispatch(removePortal({ id: itemId }, onDelSuccess, onError));
    };

    const onDelSuccess = (data) => {
        dispatch(userView(id, onSuccess, onError));
        successToast(data?.data?.message);
    };

    const fetchSessions = useCallback(async () => {
        setLoadingSessions(true);
        try {
            const res = await adminGetUserSessionsApi(id);
            if (res?.data?.data?.sessions) setSessions(res.data.data.sessions);
        } catch (_) {} finally {
            setLoadingSessions(false);
        }
    }, [id]);

    const fetchHistory = useCallback(async (page) => {
        setLoadingHistory(true);
        try {
            const res = await adminGetUserSessionHistoryApi(id, page);
            if (res?.data?.data) setHistoryData(res.data.data);
        } catch (_) {} finally {
            setLoadingHistory(false);
        }
    }, [id]);

    useEffect(() => { fetchSessions(); }, [fetchSessions]);
    useEffect(() => { if (activeTab === 'history') fetchHistory(historyPage); }, [activeTab, historyPage, fetchHistory]);

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        if (tab === 'history') setHistoryPage(1);
    };

    const handleTerminateSession = async (sessionId) => {
        setTerminatingId(sessionId);
        try {
            const res = await adminTerminateSessionApi(id, sessionId);
            if (res?.data?.statusCode === 200) {
                successToast(res.data.message);
                fetchSessions();
            } else {
                errorToast(res?.data?.message || 'Failed');
            }
        } catch (_) {
            errorToast('Failed to terminate session');
        } finally {
            setTerminatingId(null);
        }
    };

    const handleTerminateAll = async () => {
        setTerminatingAll(true);
        try {
            const res = await adminTerminateAllSessionsApi(id);
            if (res?.data?.statusCode === 200) {
                successToast(res.data.message);
                fetchSessions();
            } else {
                errorToast(res?.data?.message || 'Failed');
            }
        } catch (_) {
            errorToast('Failed to terminate sessions');
        } finally {
            setTerminatingAll(false);
        }
    };

    const displayedLogins = showAll ? userData?.userLogins : userData?.userLogins?.slice(0, 10);

    const statusBadge = (status) => {
        if (!status) return "—";
        const s = status.toLowerCase();
        const cls = s === "active" ? "bg-success" : s === "inactive" ? "bg-danger" : "bg-warning text-dark";
        return <span className={`badge ${cls}`}>{status.toUpperCase()}</span>;
    };

    const terminatedByLabel = (raw) => {
        if (!raw) return 'Inactive';
        if (raw === 'logout') return 'Logged Out';
        if (raw === 'policy') return 'Policy Enforced';
        if (raw === 'user_remote') return 'Signed Out Remotely';
        if (raw === 'expired') return 'Token Expired';
        if (raw === 'admin') return 'Terminated by Admin';
        return raw;
    };

    return (
        <div className="w-100 d-flex flex-column" style={{ height: "100%" }}>

            {/* ── Header ─────────────────────────────────────────────── */}
            <div
                className="d-flex align-items-center border-bottom px-3 position-sticky top-0 bg-white"
                style={{ height: 53, flexShrink: 0 }}
            >
                <AiOutlineArrowLeft
                    className="cursor_pointer me-2"
                    style={{ color: "#4A81E0", fontSize: 20 }}
                    onClick={() => navigate(-1)}
                />
                <span className="fs_18 fw_500">View User</span>
                <div
                    className="d-flex cursor_pointer ms-3"
                    style={{ color: "#3367D6" }}
                    onClick={() => navigate(`/edit-user/${id}`)}
                >
                    <label className="fs_20 fw_600 cursor_pointer mb-0">EDIT</label>
                </div>
            </div>

            {/* ── Content ────────────────────────────────────────────── */}
            <div style={{ overflowY: "auto", flex: 1 }}>
                {loading ? (
                    <Loading text="Loading..." />
                ) : (
                    <div className="p-3 p-md-4 text-start w-100">

                        {/* User Details */}
                        <h6 className="fw_600 fs_20 mb-3">User Details</h6>
                        <div className="card mb-4" style={{ overflow: "hidden" }}>
                            <div className="card-body p-0">
                                <table className="table mb-0 user-detail-table">
                                    <tbody>
                                        <tr>
                                            <th>ID</th>
                                            <td><code className="fs_12">{userData?.id}</code></td>
                                        </tr>
                                        <tr>
                                            <th>Email</th>
                                            <td>{userData?.email}</td>
                                        </tr>
                                        <tr>
                                            <th>User Type</th>
                                            <td>
                                                <span className={`badge ${userData?.is_admin ? "bg-primary" : "bg-secondary"}`}>
                                                    {userData?.is_admin ? "Admin" : "User"}
                                                </span>
                                            </td>
                                        </tr>
                                        <tr>
                                            <th>Status</th>
                                            <td>{statusBadge(userData?.status)}</td>
                                        </tr>
                                        <tr>
                                            <th>Sign In Method</th>
                                            <td>{userData?.sign_in?.toUpperCase() || "—"}</td>
                                        </tr>
                                        <tr>
                                            <th>Page Limit</th>
                                            <td>{userData?.page_limit ?? "—"}</td>
                                        </tr>
                                        <tr>
                                            <th>Pages Translated</th>
                                            <td>{userData?.totalNumPages || 0}</td>
                                        </tr>
                                        <tr>
                                            <th>Demo User</th>
                                            <td>{userData?.is_demo ? "Yes" : "No"}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Login Timeline */}
                        <div className="d-flex align-items-center gap-3 mb-3">
                            <h6 className="fw_600 fs_20 mb-0">Login Timeline</h6>
                            <button
                                className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
                                onClick={downLoadReport}
                            >
                                <i className="bi bi-download" /> Download Report
                            </button>
                        </div>
                        <div className="card mb-4" style={{ overflow: "hidden" }}>
                            <div className="table-responsive">
                                <table className="table table-hover mb-0 user-card-table">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Login Via</th>
                                            <th className="text-nowrap">Login Time</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {displayedLogins?.length > 0 ? (
                                            <>
                                                {displayedLogins.map((item, index) => (
                                                    <tr key={index}>
                                                        <td>{item?.type === "application" ? "Application" : "Portal"}</td>
                                                        <td className="text-nowrap text-muted">
                                                            {moment(item?.login_time).format("MMM D, YYYY h:mm A")}
                                                        </td>
                                                    </tr>
                                                ))}
                                                {userData?.userLogins?.length > 10 && (
                                                    <tr>
                                                        <td colSpan="2" className="text-end py-2">
                                                            <button className="btn btn-link btn-sm p-0" onClick={() => setShowAll(p => !p)}>
                                                                {showAll ? "Show Less" : `Show More (${userData.userLogins.length - 10} more)`}
                                                            </button>
                                                        </td>
                                                    </tr>
                                                )}
                                            </>
                                        ) : (
                                            <tr>
                                                <td colSpan="2" className="text-center text-muted py-4">No login records</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Portals */}
                        <h6 className="fw_600 fs_20 mb-3">Portals</h6>
                        <div className="card mb-4" style={{ overflow: "hidden" }}>
                            <div className="table-responsive">
                                <table className="table table-hover mb-0 user-card-table">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Portal Name</th>
                                            <th className="text-nowrap">Price Tier</th>
                                            <th className="text-nowrap">Created At</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {userData?.portalUsers?.length > 0 ? (
                                            userData.portalUsers.map((item, index) => (
                                                <tr key={index}>
                                                    <td>{item?.portal?.portal_name}</td>
                                                    <td>
                                                        <span className={`badge ${item?.portal?.price_tier === "advance" ? "bg-primary" : "bg-secondary"}`}>
                                                            {item?.portal?.price_tier?.toUpperCase()}
                                                        </span>
                                                    </td>
                                                    <td className="text-nowrap text-muted">
                                                        {moment(item?.createdAt).format("MMM D, YYYY h:mm A")}
                                                    </td>
                                                    <td>
                                                        <button
                                                            className="btn btn-sm btn-outline-danger"
                                                            onClick={() => handleRemove(item?.id)}
                                                        >
                                                            Remove
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="4" className="text-center text-muted py-4">No portals assigned</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Sessions */}
                        <div className="d-flex align-items-center justify-content-between mb-3">
                            <div className="d-flex gap-2">
                                <button
                                    className={`btn btn-sm ${activeTab === 'active' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                    onClick={() => handleTabChange('active')}
                                >
                                    Active Sessions{sessions.length > 0 ? ` (${sessions.length})` : ''}
                                </button>
                                <button
                                    className={`btn btn-sm ${activeTab === 'history' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                    onClick={() => handleTabChange('history')}
                                >
                                    History{historyData.total > 0 ? ` (${historyData.total})` : ''}
                                </button>
                            </div>
                            {activeTab === 'active' && (
                                <button
                                    className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1"
                                    onClick={handleTerminateAll}
                                    disabled={terminatingAll || sessions.length === 0}
                                >
                                    {terminatingAll
                                        ? <span className="spinner-border spinner-border-sm" />
                                        : <i className="bi bi-shield-x" />
                                    }
                                    Terminate All
                                </button>
                            )}
                        </div>

                        {activeTab === 'active' && (
                            <div className="card mb-4" style={{ overflow: 'hidden' }}>
                                {loadingSessions ? (
                                    <div className="p-3"><Loading text="Loading sessions..." /></div>
                                ) : (
                                    <div className="table-responsive">
                                        <table className="table table-hover mb-0 user-card-table">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Device</th>
                                                    <th>IP Address</th>
                                                    <th className="text-nowrap">Last Active</th>
                                                    <th></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {sessions.length > 0 ? sessions.map((s) => (
                                                    <tr key={s.id}>
                                                        <td>{s.device_summary || 'Unknown Device'}</td>
                                                        <td className="text-muted">{s.ip_address || '—'}</td>
                                                        <td className="text-nowrap text-muted">{moment(s.last_active_at).fromNow()}</td>
                                                        <td>
                                                            <button
                                                                className="btn btn-sm btn-outline-danger"
                                                                onClick={() => handleTerminateSession(s.id)}
                                                                disabled={terminatingId === s.id}
                                                            >
                                                                {terminatingId === s.id
                                                                    ? <span className="spinner-border spinner-border-sm" />
                                                                    : 'Terminate'
                                                                }
                                                            </button>
                                                        </td>
                                                    </tr>
                                                )) : (
                                                    <tr>
                                                        <td colSpan="4" className="text-center text-muted py-4">No active sessions</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        )}

                        {activeTab === 'history' && (
                            <div className="card mb-4" style={{ overflow: 'hidden' }}>
                                {loadingHistory ? (
                                    <div className="p-3"><Loading text="Loading history..." /></div>
                                ) : (
                                    <>
                                        <div className="table-responsive">
                                            <table className="table table-hover mb-0 user-card-table">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>Device</th>
                                                        <th>IP Address</th>
                                                        <th className="text-nowrap">Last Active</th>
                                                        <th className="text-nowrap">Ended</th>
                                                        <th>Reason</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {historyData.sessions.length > 0 ? historyData.sessions.map((s) => (
                                                        <tr key={s.id}>
                                                            <td>{s.device_summary || 'Unknown Device'}</td>
                                                            <td className="text-muted">{s.ip_address || '—'}</td>
                                                            <td className="text-nowrap text-muted">{moment(s.last_active_at).fromNow()}</td>
                                                            <td className="text-nowrap text-muted">{s.terminated_at ? moment(s.terminated_at).format('MMM D, YYYY h:mm A') : '—'}</td>
                                                            <td><span className="badge bg-secondary">{terminatedByLabel(s.terminated_by)}</span></td>
                                                        </tr>
                                                    )) : (
                                                        <tr>
                                                            <td colSpan="5" className="text-center text-muted py-4">No session history</td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                        {historyData.totalPages > 1 && (
                                            <div className="d-flex align-items-center justify-content-between px-3 py-2 border-top">
                                                <span className="text-muted fs_12">
                                                    Page {historyData.page} of {historyData.totalPages} ({historyData.total} total)
                                                </span>
                                                <div className="d-flex gap-2">
                                                    <button
                                                        className="btn btn-sm btn-outline-secondary"
                                                        onClick={() => setHistoryPage(p => p - 1)}
                                                        disabled={historyData.page <= 1}
                                                    >Prev</button>
                                                    <button
                                                        className="btn btn-sm btn-outline-secondary"
                                                        onClick={() => setHistoryPage(p => p + 1)}
                                                        disabled={historyData.page >= historyData.totalPages}
                                                    >Next</button>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        )}

                    </div>
                )}
            </div>
        </div>
    );
};

export default UserView;
